import { diffWords } from 'diff';

import type { APITeoMarker } from '../TeoApi';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { TeoText } from './TeoText';

export type DiffMarkersEvents = {
  markersUpdated: (detail: APITeoMarker[]) => void;
};

export class DiffMarkers {
  public events = new TeoEventTarget<DiffMarkersEvents>();
  private _compareString?: string;
  private _showing = false;

  private _textChangedTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private text: TeoText) {
    this.text.events.addEventListener('textChangedExternal', () => {
      this.handleTextChange(100);
    });
    this.text.events.addEventListener('textChanged', () => {
      this.handleTextChange();
    });
  }

  private handleTextChange(timeout: number = 500): void {
    if (this._textChangedTimeout !== null) {
      clearTimeout(this._textChangedTimeout);
      this._textChangedTimeout = null;
    }
    this._textChangedTimeout = setTimeout(() => {
      this._textChangedTimeout = null;
      this.update();
    }, timeout);
  }

  update(compareString?: string, show?: boolean): void {
    if (compareString !== this._compareString) {
      if (compareString !== undefined) {
        this._compareString = compareString;
      }
      const markers = this.createDiffMarkers();
      this.events.triggerEvent('markersUpdated', markers);
    }
    if (show === undefined) {
      show = this._showing;
    }
    if (show) {
      this.show();
    } else {
      this.hide();
    }
  }

  show(): void {
    this._showing = true;
    if (!this.text.markers.checkMarkerVisibility('editor_diff')) {
      this.text.markers.addMarkers(['editor_diff']);
    }
  }

  hide(): void {
    this._showing = false;
    if (this.text.markers.checkMarkerVisibility('editor_diff')) {
      this.text.markers.removeMarkers(['editor_diff']);
    }
  }

  private createDiffMarkers(): APITeoMarker[] {
    if (!this._compareString) {
      console.warn('No compare string provided for diff markers.');
      return [];
    }
    const currentText = this.text.text;
    const diff = diffWords(this._compareString || '', currentText || '');
    const markers: APITeoMarker[] = [];
    let offset = 0;

    for (const part of diff) {
      if (part.added) {
        markers.push({
          marker: 'editor_diff',
          offset,
          length: part.value.length,
          relation: null,
          groupName: 'diff',
          color: 'correct',
        });
      } else if (part.removed) {
        // For now, we ignore removed parts
      }
      if (!part.removed) {
        offset += part.value.length;
      }
    }
    return markers;
  }
}
